import React, { useEffect, useState } from "react";
import "./ManageLinks.css";
import Popup from "../../Popup/Popup";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";

const ManageLinks = () => {
  const [links, setLinks] = useState([]);
  const [totalClicks, setTotalClicks] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  const [popup, setPopup] = useState({ show: false, message: "", type: "" });

  const navigate = useNavigate();
  const token = sessionStorage.getItem("token");

  const fetchLinks = () => {
    setLoading(true);
    fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/links`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        setLinks(data.data || []);
        setTotalClicks(data.totalClicks || 0);
      })
      .catch(() => {
        setPopup({
          show: true,
          message: "Could not load links. Please try again.",
          type: "error",
        });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchLinks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copyLink = async (id, url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch {
      setCopiedId(null);
    }
  };

  const handleDelete = async (link) => {
    const result = await Swal.fire({
      title: "Delete this link?",
      text: "The short link will stop working for anyone who has it.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#800080",
      confirmButtonText: "Yes, delete it",
    });
    if (!result.isConfirmed) return;

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/deleteLink`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: link._id }),
        }
      );
      if (!res.ok) throw new Error("delete failed");
      setLinks((prev) => prev.filter((l) => l._id !== link._id));
      setPopup({ show: true, message: "Link deleted.", type: "success" });
    } catch {
      setPopup({
        show: true,
        message: "Could not delete the link. Please try again.",
        type: "error",
      });
    }
  };

  const formatDate = (d) =>
    new Date(d).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  const filtered = links.filter((l) => {
    const q = search.toLowerCase();
    return (
      (l.trekName || "").toLowerCase().includes(q) ||
      l.shortUrl.toLowerCase().includes(q) ||
      l.longUrl.toLowerCase().includes(q)
    );
  });

  return (
    <div className="manageLinks">
      {/* Header */}
      <div className="mlHeader">
        <div>
          <h1>Created Links</h1>
          <p>Track every shareable trek link and how many times it was opened.</p>
        </div>
        <button className="primaryBtn" onClick={() => navigate("/createtreck")}>
          + Create New Link
        </button>
      </div>

      {/* Stats */}
      <div className="statGrid">
        <div className="statCard">
          <span className="statLabel">Total Links</span>
          <span className="statValue">{links.length}</span>
        </div>
        <div className="statCard statClicks">
          <span className="statLabel">Total Clicks</span>
          <span className="statValue">{totalClicks}</span>
        </div>
      </div>

      {/* Search */}
      <div className="mlSearch">
        <span className="searchIcon">🔍</span>
        <input
          type="text"
          placeholder="Search by trek name or URL…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Link cards */}
      {loading ? (
        <p className="mlEmpty">Loading links…</p>
      ) : filtered.length === 0 ? (
        <p className="mlEmpty">
          {links.length === 0
            ? "No links created yet. Create your first trek link to get started."
            : "No links match your search."}
        </p>
      ) : (
        <div className="linkGrid">
          {filtered.map((link) => (
            <div className="linkCard" key={link._id}>
              <div className="linkCardTop">
                <h3 title={link.trekName}>{link.trekName || "Untitled Trek"}</h3>
                <span className="clickBadge">{link.clicks} clicks</span>
              </div>

              <div className="shortUrlRow">
                <input type="text" value={link.shortUrl} readOnly />
                <button
                  className="copyBtn"
                  onClick={() => copyLink(link._id, link.shortUrl)}
                >
                  {copiedId === link._id ? "Copied!" : "Copy"}
                </button>
              </div>

              <div className="linkMeta">
                <span>Created {formatDate(link.createdAt)}</span>
              </div>

              <div className="linkCardActions">
                <a
                  href={link.shortUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="openLink"
                >
                  Open ↗
                </a>
                <button
                  className="deleteLink"
                  onClick={() => handleDelete(link)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {popup.show && (
        <Popup
          message={popup.message}
          type={popup.type}
          onClose={() => setPopup({ show: false, message: "", type: "" })}
        />
      )}
    </div>
  );
};

export default ManageLinks;
